import {
    Typography,
    Box,
    Paper,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useState, useEffect } from 'react';
import { ApiCall, ApiCallFilterType, monitorApi } from '../services/monitor-api.service';
import { ApiCallDetailsDialog, getStatusCodeColor } from './ApiCallDetailsDialog';

const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('sv-SE'); // Generates ISO-like format
};

interface ApiCallsListProps {
    refreshTrigger?: number;
}

export function ApiCallsList({ refreshTrigger }: ApiCallsListProps) {
    const [apiCalls, setApiCalls] = useState<ApiCall[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [selectedApiCallId, setSelectedApiCallId] = useState<number | null>(null);
    const [filter, setFilter] = useState<ApiCallFilterType>('all');
    const [search, setSearch] = useState<string>('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const fetchApiCalls = async (
        cursor?: string,
        size: number = 25,
        reset: boolean = false,
        currentFilter?: ApiCallFilterType,
        currentSearch?: string,
    ) => {
        try {
            setLoading(true);
            setError(null);

            const response = await monitorApi.getApiCalls(
                size,
                cursor,
                currentFilter || filter,
                currentSearch !== undefined ? currentSearch : search,
            );

            if (reset || !cursor) {
                setApiCalls(response.apiCalls);
                setPage(0);
            } else {
                setApiCalls((prev) => [...prev, ...response.apiCalls]);
            }

            setHasMore(response.pagination.hasMore);
            setNextCursor(response.pagination.nextCursor);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch API calls');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApiCalls();
    }, []);

    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            fetchApiCalls(undefined, pageSize, true);
        }
    }, [refreshTrigger, pageSize]);

    const handlePageChange = (_: unknown, newPage: number) => {
        setPage(newPage);

        // If we need more data and don't have it yet
        const neededApiCalls = (newPage + 1) * pageSize;
        if (neededApiCalls > apiCalls.length && hasMore && !loading) {
            fetchApiCalls(nextCursor || undefined, pageSize);
        }
    };

    const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPageSize(newPageSize);
        setPage(0);
        fetchApiCalls(undefined, newPageSize, true);
    };

    const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: ApiCallFilterType | null) => {
        if (newFilter !== null) {
            setFilter(newFilter);
            setPage(0);
            fetchApiCalls(undefined, pageSize, true, newFilter, search);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            setPage(0);
            fetchApiCalls(undefined, pageSize, true, filter, search);
        }
    };

    const handleSearchClear = () => {
        setSearch('');
        setPage(0);
        fetchApiCalls(undefined, pageSize, true, filter, '');
    };

    const handleViewDetails = (apiCall: ApiCall) => {
        setSelectedApiCallId(apiCall.id);
    };

    const handleCloseDetails = () => {
        setSelectedApiCallId(null);
    };

    const handleRowDoubleClick = (apiCall: ApiCall) => {
        setSelectedApiCallId(apiCall.id);
    };

    const displayedApiCalls = apiCalls.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <>
            {/* Filter Controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <ToggleButtonGroup color="primary" size="small" value={filter} exclusive onChange={handleFilterChange}>
                        <ToggleButton value="all">{isMobile ? 'All' : 'All Calls'}</ToggleButton>
                        <ToggleButton value="success">Success</ToggleButton>
                        <ToggleButton value="failure">Failure</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={search}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        sx={{ width: { xs: '100%', sm: 200 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleSearchClear} edge="end">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : `${apiCalls.length} API calls loaded`}
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 700 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ py: 1 }}>ID</TableCell>
                                <TableCell sx={{ py: 1 }}>Method</TableCell>
                                <TableCell sx={{ py: 1 }}>Path</TableCell>
                                <TableCell sx={{ py: 1 }}>Status</TableCell>
                                <TableCell sx={{ py: 1 }}>Description</TableCell>
                                <TableCell sx={{ py: 1 }}>Originator</TableCell>
                                <TableCell sx={{ py: 1 }}>Created</TableCell>
                                <TableCell sx={{ py: 1 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedApiCalls.map((apiCall) => (
                                <TableRow
                                    key={apiCall.id}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': { backgroundColor: 'action.hover' },
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                    }}
                                    onDoubleClick={() => handleRowDoubleClick(apiCall)}
                                >
                                    <TableCell>{apiCall.id}</TableCell>
                                    <TableCell>
                                        <Chip label={apiCall.method} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            maxWidth: 400,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {apiCall.path}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={apiCall.statusCode}
                                            color={getStatusCodeColor(apiCall.statusCode) as any}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: '0.875rem',
                                            maxWidth: 300,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {apiCall.description || '-'}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            maxWidth: 200,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {apiCall.originator || '-'}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                                        {formatDate(apiCall.createdTime)}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(apiCall)}
                                                    color="primary"
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                        <CircularProgress size={24} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading API calls...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {!loading && displayedApiCalls.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography color="text.secondary">No API calls found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={apiCalls.length + (hasMore ? 1 : 0)} // Add 1 if there are more to indicate more data
                    rowsPerPage={pageSize}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePageSizeChange}
                />
            </Paper>

            <ApiCallDetailsDialog
                apiCallId={selectedApiCallId}
                open={!!selectedApiCallId}
                onClose={handleCloseDetails}
            />
        </>
    );
}
